<sh-droppanel
    [inOverflownContainer]="inOverflownContainer"
    [height]="height"
    [openDirection]="openDirection"
    [droppanelStyle]="droppanelStyle"
    [isOpen$]="isOpen$"
    [hasOptions$]="hasOptions$"
    [limitHeightToViewport$]="limitHeightToViewport$"
    [loading$]="loading$"
    [focusMode]="focusIndex >= 0"
>
    <div droppanelToggle #toggleContainer (keyup)="handleDeletePress($event)">
        <ng-content></ng-content>
    </div>

    @if (search && !autocomplete) {
        <div class="sh-droppanel__search" header>
            <div class="sh-input-field-with-icon">
                <input
                    #searchInput
                    type="search"
                    autocomplete="off"
                    [id]="id + '-search-input'"
                    [formControl]="searchControl"
                    [placeholder]="searchPlaceholderMsg"
                    class="sh-input-field sh-input-field-with-icon__input sh-droppanel__search__input"
                    (focus)="onSearchFocus()"
                    (mouseenter)="onHoverSearch()"
                />
                <svg sh-icon class="sh-input-field-with-icon__icon" [name]="'search'" [size]="sizes.SMALL" aria-hidden="true"></svg>
            </div>
        </div>
    }

    @if (autocomplete) {
        <div class="sh-droppanel__search" header>
            <div class="sh-input-field-with-icon">
                <sh-autocomplete
                    style="width: 100%"
                    [autocompleteFct]="autocompleteFct"
                    [clearMarginModifiers]="clearMarginModifiers"
                    [multiple]="multiple"
                    [selectedValues]="selectedValues$ | async"
                    (initialAutocompleteOptions)="receiveInitialAutocompleteOptions($event)"
                    (suggestionsSelected)="receiveSelectedSuggestion($event)"
                >
                    <input
                        type="text"
                        autocomplete="off"
                        [id]="id + '-autocomplete-input'"
                        [formControl]="autocompleteInputControl"
                        [placeholder]="searchPlaceholderMsg"
                        (mouseenter)="onHoverSearch()"
                        class="sh-input-field sh-input-field-with-icon__input sh-droppanel__search__input"
                    />
                </sh-autocomplete>
                <svg sh-icon class="sh-input-field-with-icon__icon" [name]="'search'" [size]="sizes.SMALL" aria-hidden="true"></svg>
            </div>
        </div>
    }

    @if (!(loading$ | async)) {
        @if (cappedFilteredOptions$ | async; as cappedFilteredOptions) {
            <ul #optionContainer shChildIndex class="sh-droppanel__list" [id]="id + '-list'">
                @if (!autocompleteInputControl.value) {
                    @for (option of cappedFilteredOptions; let index = $index; track trackByFn($index, option)) {
                        <ng-container
                            [ngTemplateOutlet]="multiple ? checkbox : noCheckbox"
                            [ngTemplateOutletContext]="{ option: option, index: index }"
                        ></ng-container>
                    }
                    <sh-action-item
                        [ngStyle]="{ display: listCappedMsg && (showExtraItemsLabelIndicator$ | async) ? 'block' : 'none' }"
                        class="sh-droppanel__list__item sh-droppanel__list-action"
                        [bubbleEvent]="false"
                        [item]="loadMoreActionItem"
                        [disableArrowNavigation]="true"
                        (mouseenter)="onHover($event)"
                    ></sh-action-item>
                }
                @if (!(loading$ | async) && cappedFilteredOptions.length === 0) {
                    @if (noResultsActionItem) {
                        <sh-action-item
                            class="sh-droppanel__list__item sh-droppanel__list-action sh-no-results-action"
                            [item]="noResultsActionItem"
                            (keyup.enter)="setDropdownClosed()"
                            (click)="setDropdownClosed()"
                            [disableArrowNavigation]="true"
                            (mouseenter)="onHover($event)"
                        ></sh-action-item>
                    }
                    @else {
                        <ng-container *ngTemplateOutlet="noResultsFoundTextMsg"></ng-container>
                    }
                    <ng-template #noResultsFoundTextMsg>
                        <li class="sh-droppanel__list__item sh-droppanel__list-action sh-no-pointer-events">
                            <i>{{ noResultsFoundMsg }}</i>
                        </li>
                    </ng-template>
                }
            </ul>
        }
    }
    @else {
        <ng-container *ngTemplateOutlet="loading"></ng-container>
    }
</sh-droppanel>

<ng-template #loading>
    <ul class="sh-droppanel__list" [id]="id + '-list'">
        <li class="sh-droppanel__list__item sh-droppanel__list-action sh-no-pointer-events">
            <i>{{ loadingMsg }}</i>
        </li>
    </ul>
</ng-template>

<ng-template #checkbox let-option="option" let-index="index">
    <li
        class="sh-droppanel__list__item"
        [class.sh-visually-hidden]="loading$ | async"
        [title]="option.disabled && option.disabledInfoMsg ? option.disabledInfoMsg : ''"
    >
        @let el = { state: getState(option) };
        <label
            tabindex="-1"
            class="sh-droppanel__list-action"
            [for]="id + '-option-' + index"
            [class.-sh-disabled]="isDisabled(option, el.state)"
            (keydown.enter)="toggleCheckbox($event, option)"
            (keyup.space)="toggleCheckbox($event, option)"
            (mouseenter)="onHover($event)"
        >
            <input
                type="checkbox"
                tabindex="-1"
                [id]="id + '-option-' + index"
                class="sh-checkbox sh-droppanel__list-action__checkbox"
                [checked]="el.state === states.SET"
                [shIndeterminate]="el.state === states.INDETERMINATE"
                [class.-sh-disabled]="isDisabled(option, el.state)"
                [disabled]="isDisabled(option, el.state)"
                (change)="toggleCheckbox($event, option)"
            />
            <span class="sh-droppanel__list-action__label">
                @switch (el.state) {
                    @case (states.INDETERMINATE) {
                        <i style="padding-right: 0.5rem">{{ option.label }}</i>
                    }
                    @case (states.SET) {
                        <b>{{ option.label }}</b>
                    }
                    @default {
                        {{ option.label }}
                    }
                }
            </span>
        </label>
    </li>
</ng-template>

<ng-template #noCheckbox let-option="option">
    @let el = { state: getState(option) };
    <li
        tabindex="-1"
        class="sh-droppanel__list__item sh-droppanel__list-action"
        [class.sh-visually-hidden]="loading$ | async"
        [class.-sh-disabled]="option.disabled"
        [title]="option.disabled && option.disabledInfoMsg ? option.disabledInfoMsg : option.label"
        (click)="selectSingleValue(option)"
        (keydown.enter)="selectSingleValue(option)"
        (mouseenter)="onHover($event)"
    >
        <span class="sh-droppanel__list-action__label">
            @if (el.state === states.SET) {
                <b>{{ option.label }}</b>
            }
            @if (el.state === states.INDETERMINATE) {
                <i style="padding-right: 0.5rem">{{ option.label }}</i>
            }
            @if(el.state === states.UNSET) {
                {{ option.label }}
            }
        </span>
    </li>
</ng-template>
